import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { format, addDays } from 'date-fns';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { useSpaces } from '../contexts/SpacesContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { cancelBooking, markRefunded, addCreditNoteRef } from '../services/bookingService';
import { sendRefundConfirmation, sendAccessCodeNotification, generateInvoice, generateCreditNote } from '../services/emailService';

const toDate = (v) => {
  if (!v) return null;
  if (v.toDate) return v.toDate();
  return new Date(v);
};

const fmtDate = (d) => d ? format(d, 'd MMM yyyy') : '—';
const fmtDateTime = (d) => d ? format(d, 'd MMM yyyy, h:mm a') : '—';

const Section = ({ title, children }) => (
  <div className="card p-6">
    <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-400 mb-4">{title}</h3>
    {children}
  </div>
);

const Row = ({ label, value, children }) => (
  <div className="flex justify-between items-start py-2 border-b border-gray-50 last:border-0">
    <span className="text-sm text-slate-500">{label}</span>
    <span className="text-sm text-slate-800 font-medium text-right">{children || value || '—'}</span>
  </div>
);

const Badge = ({ color, children }) => {
  const colors = {
    green: 'bg-green-100 text-green-700',
    amber: 'bg-amber-100 text-amber-700',
    red: 'bg-red-100 text-red-700',
    blue: 'bg-blue-100 text-blue-700',
    gray: 'bg-gray-100 text-gray-500',
    purple: 'bg-purple-100 text-purple-700',
  };
  return <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${colors[color] || colors.gray}`}>{children}</span>;
};

const AdminBookingDetailPage = () => {
  const { bookingId } = useParams();
  const { currentUser, userProfile } = useAuth();
  const { spaces } = useSpaces();
  const [booking, setBooking] = useState(null);
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  const isAdmin = userProfile?.role === 'admin';

  useEffect(() => {
    if (!bookingId) return;
    const load = async () => {
      setLoading(true);
      try {
        const snap = await getDoc(doc(db, 'bookings', bookingId));
        if (snap.exists()) {
          const b = { id: snap.id, ...snap.data() };
          setBooking(b);
          if (b.userId) {
            const userSnap = await getDoc(doc(db, 'users', b.userId));
            if (userSnap.exists()) setMember({ id: userSnap.id, ...userSnap.data() });
          }
        }
      } catch (err) {
        console.error('Failed to load booking:', err);
      }
      setLoading(false);
    };
    load();
  }, [bookingId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-10">
        <div className="max-w-3xl mx-auto px-4">
          <div className="flex items-center justify-center gap-2 py-20 text-sm text-slate-400">
            <span className="w-4 h-4 border-2 border-brand-400 border-t-transparent rounded-full animate-spin" />
            Loading booking...
          </div>
        </div>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="min-h-screen bg-gray-50 py-10">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <p className="text-slate-500 py-20">Booking not found.</p>
          <Link to="/admin" className="text-brand-500 hover:underline">← Back to Admin</Link>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 py-10">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <p className="text-slate-500 py-20">Admin access required.</p>
        </div>
      </div>
    );
  }

  const space = (spaces || []).find((s) => s.id === booking.spaceId);
  const start = toDate(booking.startDate);
  const end = toDate(booking.endDate);
  const created = toDate(booking.createdAt);
  const isHourly = booking.pricingType === 'hourly';
  const price = booking.price || 0;
  const gst = Math.round((price / 11) * 100) / 100;
  const exGst = Math.round((price - gst) * 100) / 100;

  const statusColor = booking.status === 'confirmed' ? 'green' : booking.status === 'cancelled' ? 'red' : 'amber';
  const paymentColor = booking.paymentStatus === 'paid' ? 'green' : booking.paymentStatus === 'failed' ? 'red' : 'amber';
  const accessColor = {
    pending: 'amber', provisioned: 'blue', active: 'green', expired: 'gray', revoked: 'red',
  }[booking.accessCodeStatus] || 'gray';

  const dateDisplay = isHourly && start && end
    ? `${format(start, 'EEE d MMM yyyy, h:mm a')} – ${format(end, 'h:mm a')}`
    : start && end
      ? start.toDateString() === addDays(end, -1).toDateString()
        ? fmtDate(start)
        : `${fmtDate(start)} → ${fmtDate(addDays(end, -1))}`
      : fmtDate(start);

  // ─── Actions ─────────────────────────────────────────────────────────────

  const handleStripeRefund = async (andCancel) => {
    if (!booking.stripePaymentIntentId) { toast.error('No Stripe payment to refund.'); return; }
    if (!window.confirm(`Refund $${price}${andCancel ? ' and cancel' : ''}?`)) return;
    setProcessing(true);
    try {
      const fns = getFunctions(undefined, 'australia-southeast1');
      const processRefund = httpsCallable(fns, 'processRefund');
      const result = await processRefund({ bookingId: booking.id, stripePaymentIntentId: booking.stripePaymentIntentId });
      await markRefunded(booking.id, result.data?.refundId);
      if (andCancel) await cancelBooking(booking.id);
      if (booking.email) {
        sendRefundConfirmation({
          to: booking.email,
          name: `${booking.firstName || ''} ${booking.lastName || ''}`.trim(),
          spaceName: space?.name || booking.spaceId,
          startDate: start ? fmtDate(start) : undefined,
          price, refundType: 'stripe',
        }).catch(() => {});
      }
      if (booking.invoiceNumber) generateCreditNote(booking.id).catch(() => {});
      toast.success(andCancel ? 'Refunded and cancelled.' : 'Refund processed.');
      // Reload booking
      const snap = await getDoc(doc(db, 'bookings', bookingId));
      if (snap.exists()) setBooking({ id: snap.id, ...snap.data() });
    } catch (err) { toast.error(err.message || 'Refund failed.'); }
    setProcessing(false);
  };

  const handleSendPin = async () => {
    if (!booking.accessPin || !booking.email) return;
    setProcessing(true);
    try {
      await sendAccessCodeNotification({
        to: booking.email,
        name: `${booking.firstName || ''} ${booking.lastName || ''}`.trim(),
        phone: booking.phone || '', pin: booking.accessPin,
        spaceName: space?.name || '',
        startDate: start ? fmtDate(start) : '',
        endDate: end ? fmtDate(addDays(end, -1)) : '',
        bookingId: booking.id,
      });
      toast.success('PIN sent.');
    } catch { toast.error('Failed to send PIN.'); }
    setProcessing(false);
  };

  const handleGenerateInvoice = async () => {
    setProcessing(true);
    try {
      const res = await generateInvoice(booking.id);
      toast.success(`Invoice ${res.invoiceNumber} generated.`);
      const snap = await getDoc(doc(db, 'bookings', bookingId));
      if (snap.exists()) setBooking({ id: snap.id, ...snap.data() });
    } catch { toast.error('Failed to generate invoice.'); }
    setProcessing(false);
  };

  const handleResendInvoice = async () => {
    setProcessing(true);
    try {
      await generateInvoice(booking.id, { resend: true });
      toast.success(`Invoice ${booking.invoiceNumber} resent.`);
    } catch { toast.error('Failed to resend invoice.'); }
    setProcessing(false);
  };

  const handleCancel = async () => {
    if (!window.confirm('Cancel this booking?')) return;
    setProcessing(true);
    try {
      await cancelBooking(booking.id);
      toast.success('Booking cancelled.');
      const snap = await getDoc(doc(db, 'bookings', bookingId));
      if (snap.exists()) setBooking({ id: snap.id, ...snap.data() });
    } catch { toast.error('Failed to cancel.'); }
    setProcessing(false);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-3xl mx-auto px-4 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <Link to="/admin" className="text-sm text-brand-500 hover:underline mb-1 inline-block">← Back to Admin</Link>
            <h1 className="text-2xl font-extrabold text-slate-800">
              Booking Details
            </h1>
            <p className="text-sm text-slate-400 font-mono mt-0.5">{booking.id}</p>
          </div>
          <div className="flex gap-2">
            <Badge color={statusColor}>{booking.status}</Badge>
            {booking.refundStatus && <Badge color="purple">{booking.refundStatus}</Badge>}
          </div>
        </div>

        {/* Member */}
        <Section title="Member">
          <Row label="Name" value={`${booking.firstName || ''} ${booking.lastName || ''}`.trim() || '—'} />
          {booking.company && booking.company.trim() && <Row label="Company" value={booking.company.trim()} />}
          <Row label="Email" value={booking.email} />
          <Row label="Phone" value={booking.phone} />
          {booking.userId && (
            <Row label="User ID">
              <span className="font-mono text-xs">{booking.userId}</span>
            </Row>
          )}
          {member?.role && <Row label="Role" value={member.role} />}
        </Section>

        {/* Booking Details */}
        <Section title="Booking">
          <Row label="Space">
            <span style={{ color: space?.color }}>{space?.name || booking.spaceId}</span>
          </Row>
          {booking.seatName && <Row label="Seat" value={booking.seatName} />}
          <Row label="Pricing" value={booking.pricingType} />
          <Row label="Dates" value={dateDisplay} />
          <Row label="Created" value={fmtDateTime(created)} />
          {booking.termsAccepted && (
            <Row label="Terms Accepted" value={booking.termsAcceptedAt ? fmtDateTime(toDate(booking.termsAcceptedAt)) : 'Yes'} />
          )}
          {booking.signature && (
            <Row label="Signature">
              <img src={booking.signature} alt="Signature" className="h-10 border rounded" />
            </Row>
          )}
          {booking.adminCreated && <Row label="Admin Created" value="Yes" />}
          {booking.notes && <Row label="Notes" value={booking.notes} />}
        </Section>

        {/* Payment */}
        <Section title="Payment">
          <Row label="Amount">
            <span className="text-lg font-bold">${price.toFixed(2)}</span>
          </Row>
          {booking.originalPrice > 0 && booking.originalPrice !== price && (
            <Row label="Original Price" value={`$${booking.originalPrice}`} />
          )}
          {booking.discountAmount > 0 && <Row label="Discount" value={`-$${booking.discountAmount}`} />}
          {booking.creditsApplied > 0 && <Row label="Credits Applied" value={`${booking.creditsApplied} hr(s)`} />}
          <Row label="Ex GST" value={`$${exGst.toFixed(2)}`} />
          <Row label="GST" value={`$${gst.toFixed(2)}`} />
          <Row label="Method">
            <Badge color={booking.paymentMethod === 'card' ? 'blue' : 'amber'}>
              {booking.paymentMethod === 'card' ? 'Card' : 'Bank Deposit'}
            </Badge>
          </Row>
          <Row label="Payment Status"><Badge color={paymentColor}>{booking.paymentStatus || '—'}</Badge></Row>
          {booking.stripePaymentIntentId && (
            <Row label="Stripe Ref">
              <a
                href={`https://dashboard.stripe.com/payments/${booking.stripePaymentIntentId}`}
                target="_blank" rel="noopener noreferrer"
                className="font-mono text-xs text-brand-500 hover:underline"
              >{booking.stripePaymentIntentId}</a>
            </Row>
          )}
          {booking.refundStatus === 'refunded' && booking.refundId && (
            <Row label="Refund ID"><span className="font-mono text-xs">{booking.refundId}</span></Row>
          )}
          {booking.creditNoteRef && <Row label="Credit Note Ref" value={booking.creditNoteRef} />}
        </Section>

        {/* Invoice */}
        <Section title="Invoice">
          {booking.invoiceNumber ? (
            <>
              <Row label="Invoice Number" value={booking.invoiceNumber} />
              <Row label="Generated" value={fmtDateTime(toDate(booking.invoiceGeneratedAt))} />
              <Row label="Download">
                <a href={booking.invoiceUrl} target="_blank" rel="noopener noreferrer"
                  className="text-brand-500 hover:underline text-xs">Download PDF</a>
              </Row>
              <div className="mt-3">
                <button onClick={handleResendInvoice} disabled={processing}
                  className="text-sm text-brand-500 hover:text-brand-700 underline disabled:opacity-50">
                  Resend Invoice
                </button>
              </div>
            </>
          ) : booking.paymentMethod === 'card' ? (
            <div className="py-2">
              <p className="text-sm text-slate-400 mb-3">No invoice generated yet.</p>
              <button onClick={handleGenerateInvoice} disabled={processing}
                className="btn-primary text-sm disabled:opacity-50">
                {processing ? 'Generating...' : 'Generate Invoice'}
              </button>
            </div>
          ) : (
            <p className="text-sm text-slate-400 py-2">Bank deposit — invoiced via MYOB.</p>
          )}
          {booking.creditNoteNumber && (
            <>
              <div className="border-t border-gray-100 mt-3 pt-3">
                <Row label="Credit Note" value={booking.creditNoteNumber} />
                <Row label="Download">
                  <a href={booking.creditNoteUrl} target="_blank" rel="noopener noreferrer"
                    className="text-brand-500 hover:underline text-xs">Download PDF</a>
                </Row>
              </div>
            </>
          )}
        </Section>

        {/* Door Access */}
        {(booking.accessPin || booking.accessCodeStatus) && (
          <Section title="Door Access">
            <Row label="PIN">
              <span className="font-mono font-bold tracking-widest text-lg">{booking.accessPin || '—'}</span>
            </Row>
            <Row label="Status"><Badge color={accessColor}>{booking.accessCodeStatus || '—'}</Badge></Row>
            {booking.unifiVisitorId && (
              <Row label="UniFi Visitor ID"><span className="font-mono text-xs">{booking.unifiVisitorId}</span></Row>
            )}
            {booking.accessProvisionedAt && (
              <Row label="Provisioned" value={fmtDateTime(toDate(booking.accessProvisionedAt))} />
            )}
            {booking.accessNotifiedAt && (
              <Row label="Notified" value={fmtDateTime(toDate(booking.accessNotifiedAt))} />
            )}
            {booking.accessPin && booking.email && (
              <div className="mt-3">
                <button onClick={handleSendPin} disabled={processing}
                  className="text-sm text-brand-500 hover:text-brand-700 underline disabled:opacity-50">
                  {processing ? 'Sending...' : 'Send PIN to Member'}
                </button>
              </div>
            )}
          </Section>
        )}

        {/* Payment Schedule (bank deposit) */}
        {booking.paymentSchedule && booking.paymentSchedule.length > 0 && (
          <Section title="Payment Schedule">
            <div className="space-y-2">
              {booking.paymentSchedule.map((item, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                  <div>
                    <p className="text-sm text-slate-700">{item.label || `Payment ${i + 1}`}</p>
                    <p className="text-xs text-slate-400">Due: {item.dueDate || '—'}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-slate-800">${item.amount || 0}</p>
                    <Badge color={item.paid ? 'green' : 'amber'}>{item.paid ? 'Paid' : 'Unpaid'}</Badge>
                  </div>
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* Actions */}
        {booking.status !== 'cancelled' && (
          <Section title="Actions">
            <div className="flex flex-wrap gap-3">
              {booking.paymentMethod === 'card' && booking.stripePaymentIntentId && !booking.refundStatus && (
                <>
                  <button onClick={() => handleStripeRefund(false)} disabled={processing}
                    className="px-4 py-2 text-sm font-medium rounded-lg border border-amber-300 text-amber-700 hover:bg-amber-50 disabled:opacity-50">
                    Refund Only
                  </button>
                  <button onClick={() => handleStripeRefund(true)} disabled={processing}
                    className="px-4 py-2 text-sm font-medium rounded-lg border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-50">
                    Refund & Cancel
                  </button>
                </>
              )}
              {!booking.refundStatus && (
                <button onClick={handleCancel} disabled={processing}
                  className="px-4 py-2 text-sm font-medium rounded-lg border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-50">
                  Cancel Booking
                </button>
              )}
            </div>
          </Section>
        )}
      </div>
    </div>
  );
};

export default AdminBookingDetailPage;
